import { useRef, useState } from 'react';

import { ApiError, errorMessage } from '@/api/client';
import {
  PARTIAL_RECOVERY_PLACEHOLDERS,
  RESOLUTION_PLACEHOLDERS,
  requireReason,
  validateScopeChanges,
} from '@/api/mock/validate';
import { useConfig, useSaveConfig } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { configPath, useScopeParam } from '@/app/scope';
import { useToast } from '@/app/toasts';
import { Button, LinkButton } from '@/components/Button';
import { useFocusFirstInvalid } from '@/components/focus';
import { Field, Textarea } from '@/components/fields';
import { History } from '@/components/icons';
import { ErrorNotice, Kv, Loading, Panel } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { SCOPE_LABEL } from '@/domain/health';
import type { Scope, ScopeConfig, ScopeConfigChanges, Violation } from '@/domain/types';
import { ApproverField, ErrorSummary } from '@/features/proposals/decision';
import { explainError, useApproverName } from '@/features/proposals/decisionState';

import { isScopeConfig, isScopeConfigValue, useBeforeUnload } from './helpers';
import { PageHeader, ReasonField, StaleNotice } from './shared';

export function ScopeTemplatesPage() {
  const scope = useScopeParam();
  if (!scope) return <NotFoundPage />;
  return <ScopeTemplates scope={scope} />;
}

function ScopeTemplates({ scope }: { scope: Scope }) {
  const query = useConfig(scope, '_scope');
  if (query.isPending) return <Loading label="Loading scope templates…" />;
  if (query.isError) {
    return (
      <ErrorNotice
        title="The scope templates could not be loaded"
        message={errorMessage(query.error)}
        action={
          <Button size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  const config = query.data;
  if (!isScopeConfig(config)) return <NotFoundPage />;
  return <TemplatesForm key={scope} scope={scope} config={config} />;
}

const SAMPLE = { recovered: 'CSV Import', remaining: 'CEDE Import', component: 'CSV Import' };

function preview(template: string): string {
  return template
    .replaceAll('{recovered}', SAMPLE.recovered)
    .replaceAll('{remaining}', SAMPLE.remaining)
    .replaceAll('{component}', SAMPLE.component);
}

function TemplatesForm({ scope, config }: { scope: Scope; config: ScopeConfig }) {
  const [baseline, setBaseline] = useState<ScopeConfig>(config);
  const [partial, setPartial] = useState(config.partial_recovery_template);
  const [resolution, setResolution] = useState(config.resolution_template);
  const [reason, setReason] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ScopeConfig | null>(null);
  const mutation = useSaveConfig(scope, '_scope');
  const toast = useToast();

  const changes: ScopeConfigChanges = {};
  if (partial !== baseline.partial_recovery_template) changes.partial_recovery_template = partial;
  if (resolution !== baseline.resolution_template) changes.resolution_template = resolution;
  const changedKeys = Object.keys(changes);
  const dirty = changedKeys.length > 0 || reason.trim().length > 0;
  useBeforeUnload(dirty);
  const stale = conflict ?? (config.version !== baseline.version ? config : null);
  const disabled = mutation.isPending;

  const reset = (next: ScopeConfig) => {
    setBaseline(next);
    setPartial(next.partial_recovery_template);
    setResolution(next.resolution_template);
    setConflict(null);
    setFieldErrors({});
    setMessage(null);
  };

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    if (changedKeys.length === 0) {
      setMessage('Nothing has changed yet. Edit a template, then save.');
      return;
    }
    const violations: Violation[] = [
      ...validateScopeChanges(baseline, changes),
      ...requireReason(reason),
    ];
    if (!approver.name.trim())
      violations.push({ field: 'changed_by', message: 'Say who is making the change.' });
    if (violations.length > 0) {
      const byField: Record<string, string> = {};
      for (const v of violations) byField[v.field] ??= v.message;
      setFieldErrors(byField);
      return;
    }
    approver.remember();
    try {
      const { config: saved } = await mutation.mutateAsync({
        version: baseline.version,
        changes,
        changed_by: approver.name.trim(),
        reason: reason.trim(),
      });
      if (isScopeConfigValue(saved)) {
        toast.push({
          tone: 'success',
          title: `Saved version ${saved.version} of the ${SCOPE_LABEL[scope]} templates`,
        });
        reset(saved);
        setReason('');
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const current = error.conflict?.current;
        if (isScopeConfigValue(current)) setConflict(current);
        else setMessage(error.message);
        return;
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        breadcrumb={[
          { label: 'Configuration', to: configPath(scope) },
          { label: 'Scope templates' },
        ]}
        title={`${SCOPE_LABEL[scope]} templates`}
        actions={
          <LinkButton to={configPath(scope, '_scope', 'history')} icon={<History size={15} />}>
            Version history
          </LinkButton>
        }
      />

      <form
        ref={formRef}
        className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px] items-start"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        noValidate
      >
        <div className="flex flex-col gap-5 min-w-0">
          {stale ? (
            <StaleNotice current={stale} onLoad={() => reset(stale)} disabled={disabled} />
          ) : null}

          <Panel
            title="Partial recovery"
            description="Posted when a component recovers while others in the same incident remain affected."
            divided={false}
          >
            <Field
              label="Partial-recovery template"
              required
              error={fieldErrors.partial_recovery_template}
              hint={`Must contain ${PARTIAL_RECOVERY_PLACEHOLDERS.join(' and ')}.`}
            >
              {(props) => (
                <Textarea
                  {...props}
                  name="partial_recovery_template"
                  rows={3}
                  value={partial}
                  onChange={(e) => setPartial(e.target.value)}
                  disabled={disabled}
                />
              )}
            </Field>
            <Preview text={preview(partial)} />
          </Panel>

          <Panel
            title="Resolution"
            description="Used when an incident is resolved without its own resolution text."
            divided={false}
          >
            <Field
              label="Resolution template"
              required
              error={fieldErrors.resolution_template}
              hint={`Must contain ${RESOLUTION_PLACEHOLDERS.join(' and ')}.`}
            >
              {(props) => (
                <Textarea
                  {...props}
                  name="resolution_template"
                  rows={3}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  disabled={disabled}
                />
              )}
            </Field>
            <Preview text={preview(resolution)} />
          </Panel>

          <Panel title="Save" divided={false}>
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_240px]">
                <ReasonField
                  value={reason}
                  onChange={setReason}
                  error={fieldErrors.reason}
                  disabled={disabled}
                  placeholder="e.g. Legal asked for plainer wording."
                />
                <ApproverField
                  label="Changed by"
                  value={approver.name}
                  onChange={approver.setName}
                  error={fieldErrors.changed_by}
                  disabled={disabled}
                />
              </div>
              <ErrorSummary message={message} />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  type="submit"
                  loading={disabled}
                  disabled={Boolean(stale)}
                >
                  Save changes
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    reset(baseline);
                    setReason('');
                  }}
                  disabled={disabled || !dirty}
                >
                  Discard edits
                </Button>
              </div>
            </div>
          </Panel>
        </div>

        <aside className="flex flex-col gap-4 lg:sticky lg:top-4">
          <Panel title="This version" bodyClassName="px-5 py-4">
            <Kv
              items={[
                { label: 'Version', value: <span className="tabular">{baseline.version}</span> },
                { label: 'Updated', value: <Timestamp iso={baseline.updated_at} mode="full" /> },
                { label: 'Updated by', value: baseline.updated_by },
              ]}
            />
          </Panel>
        </aside>
      </form>
    </div>
  );
}

function Preview({ text }: { text: string }) {
  return (
    <div className="mt-3 rounded-sm border border-dashed border-line-strong bg-surface-2 px-3 py-2.5 text-body">
      <p className="text-meta font-medium text-ink-3 mb-1">Preview</p>
      <p className="text-ink break-words">{text.trim() ? text : '—'}</p>
    </div>
  );
}
